import type { LocalInterview } from '@Domain/Ports/LocalInterviews/LocalInterviewPort';
import useLocalInterviewPort from '@UI/Providers/PortsProvider/Hooks/LocalInterviews/useLocalInterviewPort';
import * as React from 'react';

/**
 * Reflects the local interview port's store through `useSyncExternalStore`. The stable-snapshot
 * invariant that `useSyncExternalStore` requires is carried by the port's `snapshot()` itself, which
 * returns the same array reference until the store actually changes — this hook only wires the port,
 * it never needs to memoize the snapshot on top of it.
 */
function useLocalInterviews(): readonly LocalInterview[] {
	const localInterviewPort = useLocalInterviewPort();

	const subscribe = React.useCallback(
		(onStoreChange: () => void): (() => void) => localInterviewPort.subscribe(() => onStoreChange()),
		[localInterviewPort],
	);

	const getSnapshot = React.useCallback(
		(): readonly LocalInterview[] => localInterviewPort.snapshot(),
		[localInterviewPort],
	);

	return React.useSyncExternalStore(subscribe, getSnapshot);
}

export default useLocalInterviews;
